import { Edit } from '@mui/icons-material';
import { Grid2, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { RevenueType } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useGetRevenueTypesQuery, useRevenueTypesMutation } from 'api/hooks/billing/revenueType';
import { CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { DATEFORMATWITHTIME } from 'app/containers/Billing/utils';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { RevenueTypeDrawer } from './RevenueTypeDrawer';

export const RevenueTypeSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [revenueType, setRevenueType] = useState<undefined | RevenueType>(undefined);

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'revenueTypeNumber', sort: 'asc' }],
    defaultPageSize: 100,
    sortKey: 'revenuetype_sort',
    paginationKeys: { page: 'revenuetype_page', pageSize: 'revenuetype_pageSize' }
  });

  const { data, isLoading, isFetching } = useGetRevenueTypesQuery({
    params: {
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const { creation, update } = useRevenueTypesMutation();

  const handleSaveClick = async value => {
    try {
      if (isSubmitting) {
        return;
      }
      if (revenueType) {
        await update.mutateAsync({
          key: revenueType.revenueTypeId,
          data: { ...value, revenueTypeNumber: parseInt(value.revenueTypeNumber) }
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.billingSettings.mutation.revenueTypeUpdateSuccess())
          )
        );
        setOpen(false);
      } else {
        await creation.mutateAsync({
          ...value,
          revenueTypeNumber: parseInt(value.revenueTypeNumber)
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.billingSettings.mutation.revenueTypeCreationSuccess())
          )
        );
      }
      setOpen(false);
    } catch (error) {
      if (revenueType) {
        dispatch(
          AlertActions.error(
            t(translations.pages.billingSettings.mutation.revenueTypeUpdateFailed())
          )
        );
      } else {
        dispatch(
          AlertActions.error(
            t(translations.pages.billingSettings.mutation.revenueTypeCreationFailed())
          )
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setRevenueType(params.row);
            setOpen(true);
          }}
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<RevenueType> => {
    return [
      {
        field: 'revenueTypeNumber',
        flex: 2,
        headerName: t(translations.pages.billingSettings.revenueType.revenueTypeNumber())
      },
      { field: 'description', flex: 2, headerName: t(translations.common.description()) },
      {
        field: 'updatedAt',
        flex: 1,
        headerName: t(translations.common.updatedAt()),
        renderCell: params => (
          <Typography variant="body2">
            {format(new Date(params.row.updatedAt), DATEFORMATWITHTIME, { locale: de })}
          </Typography>
        )
      },
      {
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <Grid2 style={{ flexGrow: 1 }}>
      <RevenueTypeDrawer
        open={open}
        setOpen={setOpen}
        revenueType={revenueType}
        handleSaveClick={handleSaveClick}
      />
      <Grid2 container justifyContent={'space-between'} paddingBottom={1}>
        <Searchfield handleSearch={handleSearchTextChange} search={search} />
        <CreateButton
          renderSkeleton={isLoading}
          onClick={() => {
            setRevenueType(undefined);
            setOpen(true);
          }}
        />
      </Grid2>
      <Grid2 container style={{ height: '80vh' }}>
        <DataGrid
          loading={isFetching}
          columns={columns}
          rows={data?.data || []}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          sortingMode="server"
          sortModel={sortingModel}
          onSortModelChange={handleSortModelChange}
          rowCount={data?.count || 0}
        />
      </Grid2>
    </Grid2>
  );
};
